import { utils } from '@easygenerator/fragment-sdk';
import eventBus from './utils/eventBus';
import { IDropdownItem } from './types/types';

const configKeys = [
  'authServiceUrl',
  'learnServiceUrl',
  'sentryDsn',
  'sentryTracesSampleRate',
  'discoveryServiceUrl',
  'mediaServiceUrl'
] as const;

export type ConfigOptions = {
  [key in (typeof configKeys)[number]]: string;
};

function objectHasProperty<X extends object, Y extends PropertyKey>(obj: X, prop: Y): obj is X & Record<Y, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

export const isConfigOptions = (config: unknown): config is ConfigOptions => {
  if (typeof config === 'object' && config) {
    return configKeys.every(elem => objectHasProperty(config, elem)
      && (typeof config[elem] === 'string' || typeof config[elem] === 'number'));
  }
  return false;
};

export default class Config {
  static getInstance() {
    if (!Config.instance) {
      Config.instance = new Config();
    }
    return Config.instance;
  }

  private static instance: Config;

  private config!: ConfigOptions;

  private constructor() {
    // This is intentional
  }

  public async initialize() {
    const config = await utils.HttpWrapper.get(`${process.env.VUE_APP_LEARN_SERVICE_URL || ''}/configz`);
    if (isConfigOptions(config)) {
      this.config = config;
      this.initializeGlobalConfig();
    }
  }

  public async getCountries(): Promise<IDropdownItem[]> {
    return (await utils.HttpWrapper.get(`${this.config.authServiceUrl}/countries`)).map(
      (country: { isoCode: string; name: string; flag: true }) => {
        return { key: country.isoCode, label: country.name, iconChar: country.flag };
      }
    );
  }

  public async getCities(countryCode: string): Promise<IDropdownItem[]> {
    return (await utils.HttpWrapper.get(`${this.config.authServiceUrl}/countries/${countryCode}/cities`)).map(
      (city: { name: string; stateCode: string }) => {
        return { key: city.name + city.stateCode + countryCode, label: city.name };
      }
    );
  }

  private initializeGlobalConfig() {
    window.discoveryServiceUrl = this.discoveryServiceUrl;
    window.eventBus = eventBus;
  }

  get authServiceUrl() {
    return this.config.authServiceUrl;
  }

  get learnServiceUrl() {
    return this.config.learnServiceUrl;
  }

  get sentry() {
    return {
      dsn: this.config.sentryDsn,
      environment: new URL(this.learnServiceUrl).hostname,
      tracesSampleRate: parseFloat(this.config.sentryTracesSampleRate),
      tracePropagationTargets: [],
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.1
    };
  }

  get discoveryServiceUrl() {
    return this.config.discoveryServiceUrl;
  }

  get mediaServiceUrl() {
    return this.config.mediaServiceUrl;
  }
}
